import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container"]
  static values = {
    autoDismiss: { type: Boolean, default: true },
    duration: { type: Number, default: 5000 }
  }

  connect() {
    this.boundHandleToastEvent = this.handleToastEvent.bind(this)
    this.boundHandleHtmlToastEvent = this.handleHtmlToastEvent.bind(this)
    document.addEventListener("toast:show", this.boundHandleToastEvent)
    document.addEventListener("toast:show:html", this.boundHandleHtmlToastEvent)
  }

  disconnect() {
    document.removeEventListener("toast:show", this.boundHandleToastEvent)
    document.removeEventListener("toast:show:html", this.boundHandleHtmlToastEvent)
  }

  handleToastEvent(event) {
    const { message, type, duration } = event.detail
    this.show(message, type, duration)
  }

  handleHtmlToastEvent(event) {
    const { html, type, duration } = event.detail
    this.showHtml(html, type, duration)
  }

  show(message, type = "info", duration = this.durationValue) {
    const toast = this.createToast(message, type)
    this.appendAndAnimate(toast, duration)
  }

  showHtml(html, type = "info", duration = this.durationValue) {
    const toast = this.createHtmlToast(html, type)
    this.appendAndAnimate(toast, duration)
  }

  appendAndAnimate(toast, duration) {
    this.containerTarget.appendChild(toast)

    requestAnimationFrame(() => {
      toast.classList.remove("toast-enter")
      toast.classList.add("toast-enter-active")
    })

    if (this.autoDismissValue && duration > 0) {
      setTimeout(() => this.dismiss(toast), duration)
    }
  }

  createHtmlToast(html, type) {
    const toast = document.createElement("div")
    toast.className = `toast toast-${type} toast-enter`
    toast.setAttribute("role", type === "error" ? "alert" : "status")
    toast.setAttribute("aria-live", type === "error" ? "assertive" : "polite")

    // Create content div with sanitized HTML (only allow safe tags)
    const contentDiv = document.createElement("div")
    contentDiv.className = "flex-1 min-w-0"
    contentDiv.innerHTML = this.sanitizeHtml(html)

    // Create dismiss button
    const dismissBtn = document.createElement("button")
    dismissBtn.type = "button"
    dismissBtn.className = "flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
    dismissBtn.setAttribute("data-action", "click->toast#dismissClick")
    dismissBtn.setAttribute("aria-label", "Dismiss")
    dismissBtn.innerHTML = `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
    </svg>`

    toast.appendChild(contentDiv)
    toast.appendChild(dismissBtn)

    if (this.autoDismissValue) {
      const progressDiv = document.createElement("div")
      progressDiv.className = "toast-progress"
      toast.appendChild(progressDiv)
    }

    return toast
  }

  // Sanitize HTML to only allow safe tags and attributes
  sanitizeHtml(html) {
    const allowedTags = ["b", "i", "em", "strong", "a", "br", "span", "p", "div"]
    const allowedAttrs = ["href", "class", "target", "rel"]

    const temp = document.createElement("div")
    temp.innerHTML = html

    const sanitize = (element) => {
      const children = Array.from(element.childNodes)

      children.forEach(child => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const tagName = child.tagName.toLowerCase()

          if (!allowedTags.includes(tagName)) {
            // Replace disallowed tag with its text content
            const text = document.createTextNode(child.textContent)
            element.replaceChild(text, child)
          } else {
            // Remove disallowed attributes
            Array.from(child.attributes).forEach(attr => {
              if (!allowedAttrs.includes(attr.name.toLowerCase())) {
                child.removeAttribute(attr.name)
              }
              // Sanitize href to prevent javascript: URLs
              if (attr.name.toLowerCase() === "href") {
                const href = attr.value.toLowerCase().trim()
                if (href.startsWith("javascript:") || href.startsWith("data:")) {
                  child.removeAttribute("href")
                }
              }
            })
            // Recursively sanitize children
            sanitize(child)
          }
        }
      })
    }

    sanitize(temp)
    return temp.innerHTML
  }

  createToast(message, type) {
    const toast = document.createElement("div")
    toast.className = `toast toast-${type} toast-enter`
    toast.setAttribute("role", type === "error" ? "alert" : "status")
    toast.setAttribute("aria-live", type === "error" ? "assertive" : "polite")

    toast.innerHTML = `
      <div class="toast-icon flex-shrink-0">
        ${this.getIcon(type)}
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-gray-900">${this.escapeHtml(message)}</p>
      </div>
      <button type="button" class="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors" data-action="click->toast#dismissClick" aria-label="Dismiss">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
      ${this.autoDismissValue ? '<div class="toast-progress"></div>' : ''}
    `

    return toast
  }

  getIcon(type) {
    const icons = {
      success: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>`,
      error: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>`,
      warning: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
      </svg>`,
      info: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>`
    }
    return icons[type] || icons.info
  }

  dismissClick(event) {
    const toast = event.target.closest(".toast")
    if (toast) this.dismiss(toast)
  }

  dismiss(toast) {
    toast.classList.remove("toast-enter-active")
    toast.classList.add("toast-exit-active")

    toast.addEventListener("transitionend", () => {
      toast.remove()
    }, { once: true })

    setTimeout(() => toast.remove(), 300)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  success(message, duration) {
    this.show(message, "success", duration)
  }

  error(message, duration) {
    this.show(message, "error", duration)
  }

  warning(message, duration) {
    this.show(message, "warning", duration)
  }

  info(message, duration) {
    this.show(message, "info", duration)
  }
}

window.showToast = function(message, type = "info", duration = 5000) {
  document.dispatchEvent(new CustomEvent("toast:show", {
    detail: { message, type, duration }
  }))
}
